import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { FourStars } from "./FourStars";

const SUGGESTIONS = [
  "Comment faire un don ?",
  "Devenir membre A.F.B.",
  "Quelles sont les actions du Président ?",
  "Où va mon don ?",
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour ! Je suis l'assistant des A.F.B. Comment puis-je vous orienter aujourd'hui ?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, je rencontre un souci. Veuillez réessayer dans un instant." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle-button"
        className="fixed bottom-6 right-6 z-50 group"
        aria-label="Ouvrir l'assistant IA"
      >
        <span className="absolute inset-0 rounded-full bg-[#D4AF37]/40 blur-xl group-hover:bg-[#D4AF37]/60 transition-all" />
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#0052CC] to-[#003D99] text-white shadow-2xl ring-2 ring-[#D4AF37]/50 hover:ring-[#D4AF37]">
          {open ? <X size={22} /> : <Sparkles size={22} />}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.22 }}
            data-testid="chat-window"
            className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-[400px] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-[#D4AF37]/30 flex flex-col overflow-hidden"
          >
            <div className="px-5 py-4 bg-gradient-to-br from-[#0052CC] to-[#0A0F1A] text-white">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
                <FourStars size={9} /> Assistant IA — A.F.B.
              </div>
              <div className="font-display text-lg mt-0.5">Bonjour, comment puis-je vous aider ?</div>
              <div className="text-xs text-white/60 mt-0.5">Propulsé par Claude Sonnet 4.5</div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0052CC] text-white rounded-br-sm"
                      : "bg-white border border-black/5 text-[#0A0F1A] rounded-bl-sm shadow-sm"
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-black/5 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-[#475569] flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> L'assistant rédige...
                  </div>
                </div>
              )}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`chat-suggestion-${s.slice(0,8).replace(/\s/g,'-')}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#0052CC]/20 text-[#0052CC] hover:bg-[#0052CC]/5 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-black/5 bg-white flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Posez votre question..."
                data-testid="chat-input"
                className="flex-1 px-3.5 py-2.5 text-sm bg-[#FAFAFA] rounded-md border border-black/10 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="chat-send-button"
                className="w-10 h-10 rounded-md bg-[#0052CC] hover:bg-[#003D99] text-white disabled:opacity-50 flex items-center justify-center transition"
                aria-label="Envoyer"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
